import { ReactNode, useEffect } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { PAGE_TRANSITION } from "../../lib/motion";

interface PageTransitionProps {
    children: ReactNode;
}

/**
 * Wraps a routed page (Resume, a future project case study — anything that is
 * a real react-router route rather than an anchor within OnePage) so entering
 * and leaving it animates instead of cutting.
 *
 * Scoped to routes on purpose: OnePage's own section-to-section movement is
 * scrolling, not a route change, so it never re-renders this component and
 * never triggers this transition — the two navigation models don't compete.
 */
export default function PageTransition({ children }: PageTransitionProps) {
    const location = useLocation();

    useEffect(() => {
        // A hash means someone followed a deep link to a section (OnePage's
        // own scroll-to-hash effect handles it); anything else starts at the
        // top rather than wherever the previous page happened to be scrolled.
        if (!location.hash) {
            window.scrollTo(0, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    return (
        <motion.div
            initial={PAGE_TRANSITION.initial}
            animate={PAGE_TRANSITION.animate}
            exit={PAGE_TRANSITION.exit}
            transition={PAGE_TRANSITION.transition}
        >
            {children}
        </motion.div>
    );
}
